import {
  Component,
  OnInit,
  inject,
  DestroyRef,
  ViewChild,
  ChangeDetectorRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormsModule,
  FormBuilder,
  FormGroup,
  FormControl,
  ReactiveFormsModule,
} from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { Subject, debounceTime, distinctUntilChanged, forkJoin, of, catchError, map } from 'rxjs';
import { ButtonModule } from 'primeng/button';
import { MenuModule } from 'primeng/menu';
import { SelectModule } from 'primeng/select';
import { InputTextModule } from 'primeng/inputtext';
import { InputNumberModule } from 'primeng/inputnumber';
import { DatePickerModule } from 'primeng/datepicker';
import { SkeletonModule } from 'primeng/skeleton';
import { CategoriaEquipService } from '@core/services/domain-services';
import { DrawerModule } from 'primeng/drawer';

import { TooltipModule } from 'primeng/tooltip';
import { ConfirmationService, MessageService, MenuItem } from 'primeng/api';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  EquipamentoService,
  PostoService,
  PropriedadeEquipService,
} from '@core/services/domain-services';
import { EmptyStateComponent } from '@shared/components/empty-state/empty-state.component';
import { PaginatorControlComponent } from '@shared/components/paginator-control/paginator-control.component';
import { BreadcrumbComponent } from '@shared/components/breadcrumb/breadcrumb.component';
import { EquipamentosNavComponent } from '../equipamentos-nav/equipamentos-nav.component';
import {
  Equipamento,
  EnumEstadoEquipamento,
  Posto,
  PropriedadeEquipamento,
} from '@core/models/models';
import { formatNomeCampo } from '@shared/utils/format-nome-campo.util';
import {
  EquipamentoKpiGridComponent,
  EquipamentoKpiItem,
} from '../components/equipamento-kpi-grid/equipamento-kpi-grid.component';
import { EquipamentoMaintenanceTabComponent } from '../components/equipamento-maintenance-tab/equipamento-maintenance-tab.component';
import { NovoEquipamentoComponent } from '../novo-equipamento/novo-equipamento.component';
import { EquipmentPhotoComponent } from '../components/equipment-photo/equipment-photo.component';
import { EquipamentoFilterStateService, EquipamentoFilters, filtrarEquipamentos, filtrosApiEquipamentos } from '../services/equipamento-filter-state.service';
import { TiposEquipamentoComponent } from '../tipos-equipamento/tipos-equipamento.component';

const TIPO_ARMAS = 1; // confirmado nos exemplos reais da API

// Filtros de propriedade aplicados client-side (API não suporta filtro por prop. dinâmica).

type PosicaoArma = 'atribuida' | 'deposito' | 'manutencao' | 'suspensa';

@Component({
  selector: 'app-armas-page',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    RouterModule,
    ButtonModule,
    MenuModule,
    SelectModule,
    SkeletonModule,

    TooltipModule,
    DrawerModule,
    EmptyStateComponent,
    PaginatorControlComponent,
    BreadcrumbComponent,
    EquipamentosNavComponent,
    EquipamentoKpiGridComponent,
    EquipmentPhotoComponent,
    EquipamentoMaintenanceTabComponent,
    NovoEquipamentoComponent,
    TiposEquipamentoComponent,
  ],

  templateUrl: './armas.component.html',
  styleUrl: './armas.component.scss',
})
export class ArmasPageComponent implements OnInit {
  @ViewChild('rowMenu') rowMenu!: any;

  private readonly svc = inject(EquipamentoService);
  private readonly propriedadeSvc = inject(PropriedadeEquipService);
  private readonly postoSvc = inject(PostoService);
  private readonly fb = inject(FormBuilder);
  private readonly msg = inject(MessageService);
  private readonly confirm = inject(ConfirmationService);
  readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly categoriaSvc = inject(CategoriaEquipService);
  private readonly filterSvc = inject(EquipamentoFilterStateService);

  equipamentos: Equipamento[] = [];
  pageFirst = 0;
  pageSize = 10;
  today = new Date();
  loading = false;
  loadingPropriedades = false;
  menuItems: MenuItem[] = [];
  showCreateDrawer = false;
  activeSection: 'list' | 'configuration' = 'list';
  totalRecords = 0;
  navTotal: number | null = null;
  searchTerm = '';
  activeQuickFilter = 'Todas';
  readonly quickFilters = ['Todas', 'Atribuídas', 'Depósito', 'Manutenção', 'Inactivas'];
  IdCategoriaEquipamento: number | null = null;
  private activeFilters: EquipamentoFilters = {};

  showAtribuirDrawer = false;
  atribuirSubmitting = false;
  armaParaAtribuir: Equipamento | null = null;
  atribuirPostoId: number | null = null;
  postos: Array<{ label: string; value: number }> = [];
  postosLoading = false;
  readonly postoNomes = new Map<string, string>();

  readonly formatNomeCampo = formatNomeCampo;

  readonly estadoOptions = [
    { label: 'Todos os estados', value: null },
    { label: 'Bom Estado', value: EnumEstadoEquipamento.BOM },
    { label: 'Inactivo', value: EnumEstadoEquipamento.INATIVO },
    { label: 'Destruído', value: EnumEstadoEquipamento.DESTRUIDO },
  ];

  ngOnInit(): void {
    this.loadPostos();
    this.filterSvc.filters$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((filters) => {
        this.activeFilters = filters;
        this.pageFirst = 0;
        this.load();
      });
  }

  load(): void {
    this.loading = true;
    const pageNumber = Math.floor(this.pageFirst / this.pageSize) + 1;

    this.svc
      .getAll({
        ...filtrosApiEquipamentos(this.activeFilters),
        IdTipoEquipamento: TIPO_ARMAS,
        pageNumber,
        pageSize: Object.keys(this.activeFilters).length ? 1000 : this.pageSize,
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          this.equipamentos = response.data ?? [];
          this.carregarNomesPostos(this.equipamentos);
          this.totalRecords = response.totalRecords ?? this.equipamentos.length;
          this.navTotal = this.totalRecords;

          if (
            this.equipamentos.length === 0 &&
            this.totalRecords > 0 &&
            this.pageFirst >= this.totalRecords
          ) {
            this.pageFirst = Math.max(0, this.pageFirst - this.pageSize);
            this.load();
            return;
          }

          this.loading = false;
          this.cdr.detectChanges();
        },
        error: () => {
          this.equipamentos = [];
          this.totalRecords = 0;
          this.navTotal = 0;
          this.loading = false;
          this.msg.add({
            severity: 'error',
            summary: 'Erro ao carregar',
            detail: 'Não foi possível carregar a listagem de equipamentos.',
            life: 3500,
          });
          this.cdr.detectChanges();
        },
      });
  }

  get filteredEquipamentos(): Equipamento[] {
    return filtrarEquipamentos(this.equipamentos, this.activeFilters).filter(
      (e) => this.matchesQuickFilter(e) && this.matchesSearch(e),
    );
  }

  get paginatedEquipamentos(): Equipamento[] {
    return this.filteredEquipamentos;
  }

  get hasLocalFilters(): boolean {
    return Boolean(this.searchTerm.trim()) || this.activeQuickFilter !== 'Todas';
  }

  setQuickFilter(filter: string): void {
    this.activeQuickFilter = filter;
  }

  private matchesQuickFilter(e: Equipamento): boolean {
    if (this.activeQuickFilter === 'Todas') return true;
    const key = this.quickFilterToKey(this.activeQuickFilter);
    return key != null && this.classifyArma(e) === key;
  }

  private quickFilterToKey(filter: string): PosicaoArma | null {
    const map: Record<string, PosicaoArma> = {
      Atribuídas: 'atribuida',
      Depósito: 'deposito',
      Manutenção: 'manutencao',
      Inactivas: 'suspensa',
    };
    return map[filter] ?? null;
  }

  private matchesSearch(e: Equipamento): boolean {
    const term = (this.searchTerm ?? '').toLowerCase().trim();
    if (!term) return true;
    const candidatos = [
      this.getPropriedadeValor(e, 'NUMERO_SERIE'),
      this.getPropriedadeValor(e, 'TIPO'),
      this.getPropriedadeValor(e, 'GUARDA_RESPONSAVEL'),
      this.getPropriedadeValor(e, 'RESPONSAVEL'),
    ];
    return candidatos.some((v) =>
      String(v ?? '').toLowerCase().includes(term),
    );
  }

  onPageChange(event: { first?: number; rows?: number }): void {
    const nextRows = event.rows ?? this.pageSize;
    this.pageFirst = nextRows !== this.pageSize ? 0 : (event.first ?? 0);
    this.pageSize = nextRows;
    this.load();
  }

  get kpiItems(): EquipamentoKpiItem[] {
    return [
      {
        icon: 'pi pi-shield',
        iconClass: 'total-red',
        value: this.kpiTotal,
        label: 'Total Armas',
      },
      {
        icon: 'ri-map-pin-2-line',
        iconClass: 'bom',
        value: this.kpiAtribuidas,
        label: 'Atribuídas (pág.)',
      },
      {
        icon: 'pi pi-warehouse',
        iconClass: 'deposito',
        value: this.kpiDeposito,
        label: 'Em Depósito (pág.)',
      },
      {
        icon: 'ri-error-warning-line',
        iconClass: 'alerta',
        value: this.kpiVerificacoesPendentes,
        label: 'Verif. Atrasadas (pág.)',
      },
      {
        icon: 'ri-money-dollar-circle-line',
        iconClass: 'valor',
        value: this.formatKz(this.kpiValor),
        label: 'Valor Total (pág.)',
      },
    ];
  }

  get kpiTotal(): number {
    return this.totalRecords;
  }
  get maintenanceCount(): number {
    return this.maintenanceApiCount;
  }
  maintenanceApiCount = 0;
  get kpiAtribuidas(): number {
    return this.equipamentos.filter(
      (e) => this.classifyArma(e) === 'atribuida',
    ).length;
  }

  get kpiDeposito(): number {
    return this.equipamentos.filter(
      (e) => this.classifyArma(e) === 'deposito',
    ).length;
  }
  get kpiVerificacoesPendentes(): number {
    const now = new Date();
    return this.paginatedEquipamentos.filter((e) => {
      const val = this.getPropriedadeValor(
        e,
        'PROXIMA_VERIFICACAO_OBRIGATORIA',
      );
      return val && new Date(val) < now;
    }).length;
  }
  get kpiValor(): number {
    return this.paginatedEquipamentos.reduce((a, e) => a + (e.valorAquisicao ?? 0), 0);
  }

  getPropriedadeValor(e: Equipamento, nome: string): string {
    const propriedade = e.propriedades?.find(
      (p) => p.nome.toUpperCase() === nome.toUpperCase(),
    );
    return propriedade?.valorDescricao ?? propriedade?.valor ?? '';
  }

  isVerificacaoAtrasada(e: Equipamento): boolean {
    const val = this.getPropriedadeValor(e, 'PROXIMA_VERIFICACAO_OBRIGATORIA');
    return !!val && new Date(val) < new Date();
  }

  verDetalhe(e: Equipamento): void {
    if (e.id) this.router.navigate(['/admin/equipamentos', e.id]);
  }

  openCreateDrawer(): void {
    if (this.IdCategoriaEquipamento !== null) {
      this.showCreateDrawer = true;
      return;
    }

    this.categoriaSvc
      .getAll({ pageSize: 100 })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          this.IdCategoriaEquipamento =
            response.data?.find((category) =>
              (category.designacao ?? '').toUpperCase().includes('ARMA'),
            )?.id ?? null;
          this.showCreateDrawer = true;
          this.cdr.detectChanges();
        },
        error: (error) => {
          console.warn('Categorias indisponíveis:', error?.status);
          this.showCreateDrawer = true;
          this.cdr.detectChanges();
        },
      });
  }

  onEquipmentSaved(): void {
    this.showCreateDrawer = false;
    this.pageFirst = 0;
    this.msg.add({
      severity: 'success',
      summary: 'Registado',
      detail: 'Arma registada com sucesso.',
      life: 3000,
    });
    this.load();
  }

  openRowMenu(event: Event, e: Equipamento): void {
    const estado = this.classifyArma(e);
    const items: MenuItem[] = [];

    items.push({
      icon: 'pi pi-eye',
      label: 'Ver Detalhe',
      command: () => this.verDetalhe(e),
    });

    items.push({
      icon: 'pi pi-pencil',
      label: 'Editar',
      command: () =>
        this.router.navigate(['/admin/equipamentos', e.id, 'editar']),
    });

    if (estado === 'atribuida' || estado === 'manutencao') {
      items.push({ separator: true });
      items.push({
        icon: 'pi pi-clipboard-check',
        label: 'Registar Verificação',
        command: () => this.registarVerificacao(e),
      });
      items.push({
        icon: 'pi pi-pause-circle',
        label: 'Suspender',
        styleClass: 'menu-danger',
        command: () => this.suspenderArma(e),
      });
    } else if (estado === 'deposito') {
      items.push({ separator: true });
      items.push({
        icon: 'pi pi-user-plus',
        label: 'Atribuir',
        command: () => this.atribuirArma(e),
      });
      items.push({
        icon: 'pi pi-pause-circle',
        label: 'Suspender',
        styleClass: 'menu-danger',
        command: () => this.suspenderArma(e),
      });
    } else if (estado === 'suspensa') {
      items.push({ separator: true });
      items.push({
        icon: 'pi pi-check-circle',
        label: 'Reactivar',
        styleClass: 'menu-success',
        command: () => this.reativarArma(e),
      });
      items.push({
        icon: 'pi pi-trash',
        label: 'Remover',
        styleClass: 'menu-danger',
        command: () => this.confirmDelete(e),
      });
    }

    this.menuItems = items;
    this.rowMenu.toggle(event);
  }

  registarVerificacao(e: Equipamento): void {
    if (!e.id) return;
    this.router.navigate(['/admin/equipamentos', e.id], {
      queryParams: { tab: 'inspecao', acao: 'registar' },
    });
  }

  suspenderArma(e: Equipamento): void {
    if (!e.id) return;
    const numeroSerie =
      this.getPropriedadeValor(e, 'NUMERO_SERIE') || e.codigoInterno || '';
    this.confirm.confirm({
      header: 'Suspender Arma',
      message: `Tem a certeza que deseja suspender a arma "${numeroSerie}"? O seu estado passará para Inactiva.`,
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Suspender',
      rejectLabel: 'Cancelar',
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => {
        this.loading = true;
        const formData = this.buildArmaUpdateFormData(e, {
          ativo: false,
          estado: EnumEstadoEquipamento.INATIVO,
        });
        this.svc
          .updateWithFormData(e.id!, formData)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe({
            next: () => {
              this.msg.add({
                severity: 'success',
                summary: 'Suspensa',
                detail: `Arma ${numeroSerie} suspensa com sucesso.`,
                life: 3000,
              });
              this.load();
            },
            error: () => {
              this.loading = false;
              this.msg.add({
                severity: 'error',
                summary: 'Erro',
                detail: 'Não foi possível suspender a arma.',
                life: 3000,
              });
              this.cdr.detectChanges();
            },
          });
      },
    });
  }

  reativarArma(e: Equipamento): void {
    if (!e.id) return;
    const numeroSerie =
      this.getPropriedadeValor(e, 'NUMERO_SERIE') || e.codigoInterno || '';
    this.confirm.confirm({
      header: 'Reactivar Arma',
      message: `Deseja reactivar a arma "${numeroSerie}"?`,
      icon: 'pi pi-check-circle',
      acceptLabel: 'Reactivar',
      rejectLabel: 'Cancelar',
      acceptButtonStyleClass: 'p-button-success',
      accept: () => {
        this.loading = true;
        const formData = this.buildArmaUpdateFormData(e, {
          ativo: true,
          estado: EnumEstadoEquipamento.BOM,
        });
        this.svc
          .updateWithFormData(e.id!, formData)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe({
            next: () => {
              this.msg.add({
                severity: 'success',
                summary: 'Reactiva',
                detail: `Arma ${numeroSerie} reactivada com sucesso.`,
                life: 3000,
              });
              this.load();
            },
            error: () => {
              this.loading = false;
              this.msg.add({
                severity: 'error',
                summary: 'Erro',
                detail: 'Não foi possível reactivar a arma.',
                life: 3000,
              });
              this.cdr.detectChanges();
            },
          });
      },
    });
  }

  private buildArmaUpdateFormData(
    e: Equipamento,
    overrides: {
      ativo?: boolean;
      estado: EnumEstadoEquipamento;
      postoId?: number | null;
    },
  ): FormData {
    // Espelha exatamente o payload que o formulário de edição envia em
    // PUT /Equipamentos/{id} (updateWithFormData), garantindo que os campos
    // obrigatórios do backend não ficam omissos em operações parciais.
    const qualquer = e as any;
    const fd = new FormData();
    fd.append('IdTipoEquipamento', String(e.idTipoEquipamento));
    if (qualquer.tipoEquipamentoEnum != null) {
      fd.append('TipoEquipamentoEnum', String(qualquer.tipoEquipamentoEnum));
    }
    fd.append('Estado', String(overrides.estado));
    const targetPostoId = overrides.postoId != null ? overrides.postoId : e.idPosto;
    if (targetPostoId != null) {
      fd.append('PostoId', String(targetPostoId));
    }
    if (e.dataAquisicao)
      fd.append(
        'DataAquisicao',
        new Date(e.dataAquisicao).toISOString().split('T')[0],
      );
    if (e.valorAquisicao != null)
      fd.append('ValorAquisicao', String(e.valorAquisicao));
    if (e.observacoes) fd.append('Observacoes', e.observacoes);
    if (overrides.ativo != null) fd.append('Ativo', String(overrides.ativo));
    let propIdx = 0;
    (e.propriedades ?? []).forEach((p) => {
      let valor = p.valor;
      const nomeProp = String(p.nome ?? '').toUpperCase();
      if ((nomeProp === 'POSTO_ATRIBUIDO' || nomeProp === 'POSTO') && targetPostoId != null) {
        valor = String(targetPostoId);
      }
      if (valor === null || valor === undefined || valor === '') return;
      fd.append(
        `Propriedades[${propIdx}].PropriedadeId`,
        String(p.propriedadeId),
      );
      fd.append(`Propriedades[${propIdx}].Valor`, String(valor));
      propIdx++;
    });
    return fd;
  }

  getPostoNome(e: Equipamento): string {
    const equipamento = e as any;
    const propriedadePosto = equipamento.propriedades?.find((p: any) =>
      ['POSTO_ATRIBUIDO', 'POSTO'].includes(String(p?.nome ?? '').toUpperCase()),
    );
    const valorDescricaoPosto = String(propriedadePosto?.valorDescricao ?? '').trim();
    const valorPosto = String(propriedadePosto?.valor ?? '').trim();
    const postoNomePropriedade = valorDescricaoPosto && Number.isNaN(Number(valorDescricaoPosto))
      ? valorDescricaoPosto
      : valorPosto && Number.isNaN(Number(valorPosto))
        ? valorPosto
        : null;
    const postoNome =
      (equipamento.posto?.nome && String(equipamento.posto.nome).trim()) ||
      (equipamento.posto?.Nome && String(equipamento.posto.Nome).trim()) ||
      equipamento.posto?.nomePosto ||
      equipamento.posto?.NomePosto ||
      equipamento.postoNome ||
      equipamento.nomePosto ||
      equipamento.PostoNome ||
      postoNomePropriedade;

    const rawPostoId =
      equipamento.idPosto ??
      equipamento.IdPosto ??
      equipamento.postoId ??
      equipamento.PostoId ??
      (equipamento.posto?.id > 0 ? equipamento.posto?.id : null) ??
      (Number.isFinite(Number(valorPosto)) && Number(valorPosto) > 0 ? valorPosto : null);

    const idStr = rawPostoId != null && rawPostoId !== '' ? String(rawPostoId) : null;
    const nomeFromMap = idStr ? this.postoNomes.get(idStr) : null;

    return postoNome || nomeFromMap || 'Sem posto';
  }

  private carregarNomesPostos(equipamentos: Equipamento[]): void {
    const ids = new Set(
      equipamentos
        .map((equipamento) => {
          const item = equipamento as any;
          const postoProp = item.propriedades?.find((p: any) =>
            ['POSTO_ATRIBUIDO', 'POSTO'].includes(String(p?.nome ?? '').toUpperCase()),
          );
          return (
            item.idPosto ??
            item.IdPosto ??
            item.postoId ??
            item.PostoId ??
            (item.posto?.id > 0 ? item.posto?.id : null) ??
            postoProp?.valor
          );
        })
        .filter((id) => id != null && id !== '')
        .map((id) => Number(id))
        .filter((id) => Number.isFinite(id) && id > 0),
    );
    const idsPorCarregar = [...ids].filter((id) => !this.postoNomes.has(String(id)));
    if (!idsPorCarregar.length) return;

    forkJoin(
      idsPorCarregar.map((id) =>
        this.postoSvc.getById(id).pipe(
          catchError(() => of(null)),
          map((posto: any) => ({ requestedId: id, posto })),
        ),
      ),
    )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((resultados: any[]) => {
        for (const { requestedId, posto } of resultados) {
          const data = posto?.data ?? posto;
          const id = data?.id ?? data?.Id ?? requestedId;
          const nome = data?.nome ?? data?.Nome ?? data?.designacao ?? data?.Designacao;
          if (id != null && nome) this.postoNomes.set(String(id), String(nome));
        }
        this.cdr.markForCheck();
      });
  }

  atribuirArma(e: Equipamento): void {
    if (!e.id) return;
    this.armaParaAtribuir = e;
    this.atribuirPostoId = null;
    this.showAtribuirDrawer = true;
    this.cdr.detectChanges();
    this.loadPostos();
  }

  private loadPostos(): void {
    if (this.postos.length > 0) return;
    this.postosLoading = true;
    this.postoSvc
      .getAll({ pageSize: 200 })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res: any) => {
          const items = Array.isArray(res?.data)
            ? res.data
            : Array.isArray(res)
              ? res
              : [];
          this.postos = items
            .map((p: Posto) => {
              const id = Number((p as any).id ?? (p as any).Id);
              const nome = p.nome || (p as any).Nome || (p as any).designacao || (p as any).Designacao;
              return { label: nome || `Posto #${id}`, value: id };
            })
            .filter((p: { value: number }) => Number.isFinite(p.value) && p.value > 0);
          items.forEach((p: any) => {
            const id = p.id ?? p.Id;
            const nome = p.nome ?? p.Nome ?? p.designacao ?? p.Designacao;
            if (id != null && nome) this.postoNomes.set(String(id), String(nome));
          });
          this.postosLoading = false;
          this.cdr.detectChanges();
        },
        error: () => {
          this.postos = [];
          this.postosLoading = false;
          this.msg.add({
            severity: 'error',
            summary: 'Erro',
            detail: 'Não foi possível carregar os postos.',
            life: 3000,
          });
          this.cdr.detectChanges();
        },
      });
  }

  closeAtribuirDrawer(): void {
    if (this.atribuirSubmitting) return;
    this.showAtribuirDrawer = false;
    this.armaParaAtribuir = null;
    this.atribuirPostoId = null;
  }

  confirmarAtribuicao(): void {
    const arma = this.armaParaAtribuir;
    if (!arma?.id || this.atribuirPostoId == null || this.atribuirSubmitting) {
      return;
    }
    const numeroSerie =
      this.getPropriedadeValor(arma, 'NUMERO_SERIE') || arma.codigoInterno || '';
    const formData = this.buildArmaUpdateFormData(arma, {
      ativo: true,
      estado: arma.estado ?? EnumEstadoEquipamento.BOM,
      postoId: this.atribuirPostoId,
    });
    this.atribuirSubmitting = true;
    this.svc
      .updateWithFormData(arma.id, formData)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.atribuirSubmitting = false;
          this.showAtribuirDrawer = false;
          this.armaParaAtribuir = null;
          this.atribuirPostoId = null;
          this.msg.add({
            severity: 'success',
            summary: 'Atribuída',
            detail: `Arma ${numeroSerie} atribuída ao posto com sucesso.`,
            life: 3000,
          });
          this.load();
        },
        error: () => {
          this.atribuirSubmitting = false;
          this.msg.add({
            severity: 'error',
            summary: 'Erro',
            detail: 'Não foi possível atribuir a arma.',
            life: 3000,
          });
          this.cdr.detectChanges();
        },
      });
  }

  confirmDelete(e: Equipamento): void {
    this.confirm.confirm({
      header: 'Remover Arma',
      message: `A arma "${e.codigoInterno}" será permanentemente removida. Deseja continuar?`,
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Remover',
      rejectLabel: 'Cancelar',
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => {
        if (!e.id) return;
        this.svc
          .delete(e.id)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe({
            next: () => {
              this.msg.add({
                severity: 'success',
                summary: 'Removida',
                detail: 'Arma removida.',
                life: 3000,
              });
              this.load();
            },
          });
      },
    });
  }

  classifyArma(e: Equipamento): PosicaoArma {
    const estado = String(e.estado ?? '').trim().toUpperCase();
    const estadoPropriedade = this.getPropriedadeValor(e, 'ESTADO').toUpperCase();

    if (
      estado === 'MANUTENCAO' ||
      estado === 'EM_MANUTENCAO' ||
      estado === 'EM MANUTENÇÃO' ||
      estadoPropriedade === 'MANUTENCAO' ||
      estadoPropriedade === 'EM_MANUTENCAO' ||
      estadoPropriedade === 'EM MANUTENÇÃO' ||
      (e as any).emManutencao === true
    ){
      return 'manutencao';
    }

    if (
      e.ativo === false ||
      estado === 'INATIVO' ||
      estado === 'INACTIVO' ||
      estado === 'SUSPENSO' ||
      estado === 'SUSPENSA' ||
      estado === 'DESTRUIDO' ||
      estado === 'DESTRUÍDO' ||
      estado === '2' ||
      estado === '3'
    ) {
      return 'suspensa';
    }

    return e.idPosto != null ? 'atribuida' : 'deposito';
  }


  getArmaEstadoLabel(e: Equipamento): string {
    const map: Record<string, string> = {
      atribuida: 'Atribuída',
      deposito: 'Em Depósito',
      manutencao: 'Em Manutenção',
      suspensa: 'Suspensa',
    };
    return map[this.classifyArma(e)] ?? '—';
  }

  getArmaEstadoClass(e: Equipamento): string {
    const map: Record<string, string> = {
      atribuida: 'badge-bom',
      deposito: 'badge-default',
      manutencao: 'badge-warning',
      suspensa: 'badge-inativo',
    };
    return map[this.classifyArma(e)] ?? '';
  }

  getResponsavel(e: Equipamento): string {
    return (
      this.getPropriedadeValor(e, 'GUARDA_RESPONSAVEL') ||
      this.getPropriedadeValor(e, 'RESPONSAVEL') ||
      '—'
    );
  }

  getEstadoClass(estado?: any): string {
    const val = String(estado ?? '').toUpperCase();
    if (val === 'BOM' || val === '1') return 'badge-bom';
    if (val === 'DESTRUIDO' || val === '2') return 'badge-destruido';
    if (val === 'INATIVO' || val === '3') return 'badge-inativo';
    return '';
  }

  getEstadoLabel(estado?: any): string {
    const val = String(estado ?? '').toUpperCase();
    if (val === 'BOM' || val === '1') return 'Bom';
    if (val === 'DESTRUIDO' || val === '2') return 'Destruído';
    if (val === 'INATIVO' || val === '3') return 'Inactivo';
    return val || '—';
  }

  formatKz(v?: number): string {
    if (v == null) return '—';
    return (
      new Intl.NumberFormat('pt-AO', { maximumFractionDigits: 0 }).format(v) +
      ' Kz'
    );
  }

  formatDate(d?: string): string {
    if (!d) return '—';
    try {
      return new Date(d).toLocaleDateString('pt-AO');
    } catch {
      return d;
    }
  }
}
